import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import type ViewType from '@/Enums/ReportingDashboard/ViewType.enum';
import { ICampaignNameReportRecord } from '@/Interfaces/ReportingDashboard/Beeswax/ReportRecord.interface';
import type { IState } from '@/Interfaces/Slices/ReportingDashboard/Beeswax/State.interface';
import { initialState } from '@/constants/reportingDashboard/beeswax';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { placementReportToControlOptions } from '@/helpers/reportingDashboard/placement.helpers';
import { fulfilledReportCaseHandler } from '@/helpers/reportingDashboard/slice';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  boundaryMap,
  getCampaignDeliveryReport,
  getPlacementDeliveryReport,
  getTotalReportData,
  reportMap,
} from './beeswaxThunks';

export const reportingDashboardBeeswax = createSlice({
  name: 'reportingDashboardBeeswax',
  initialState,
  reducers: {
    resetBeeswaxReportsLoadingState: state => {
      Object.entries(state).forEach(([key, value]) => {
        if (key.endsWith('RequestId') && !key.startsWith('campaignGraph') && !value) {
          (state[key as keyof IState] as string) = DEFAULT_LOADING_REQUEST_ID;
        }
      });
      state.errorCardMessage = '';
    },
    setBeeswaxViewType: (state, { payload }: PayloadAction<ViewType>) => {
      state.viewType = payload;
    },
  },
  extraReducers: builder => {
    reportMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, pendingCaseHandler(key, []))
        .addCase(thunk.rejected, rejectedCaseHandler(key, []))
        .addCase(thunk.fulfilled, fulfilledReportCaseHandler(key));
    });

    boundaryMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, pendingCaseHandler(key))
        .addCase(thunk.rejected, rejectedCaseHandler(key))
        .addCase(thunk.fulfilled, fulfilledCaseHandler(key));
    });

    builder
      .addCase(getTotalReportData.pending, pendingCaseHandler('totals'))
      .addCase(getTotalReportData.rejected, (state, action) => {
        if (state.totalsRequestId === action.meta.requestId) {
          state.errorCardMessage = 'There seems to be a momentary issue loading your data. Please retry.';
          state.totalsRequestId = null;
        }
      })
      // todo: empty data handling
      .addCase(getTotalReportData.fulfilled, (state, action) => {
        if (state.totalsRequestId === action.meta.requestId) {
          const { totals } = action.payload;

          state.totalsRequestId = null;
          state.totals = totals;
          if (!totals[BeeswaxReportProperties.IMPRESSIONS]) {
            state.errorCardMessage = "We couldn't find any data. Please adjust your filters and try again.";
          }
        }
      })
      .addCase(getCampaignDeliveryReport.pending, pendingCaseHandler('campaignDeliveryReport'))
      .addCase(getCampaignDeliveryReport.rejected, rejectedCaseHandler('campaignDeliveryReport', []))
      .addCase(getCampaignDeliveryReport.fulfilled, (state, action) => {
        const { report, filters } = action.payload;
        if (state.campaignDeliveryReportRequestId === action.meta.requestId) {
          state.campaignDeliveryReportRequestId = null;
          state.campaignDeliveryReport = report;
        }
        if (filters?.campaigns?.length === 0) {
          state.campaigns = (report as ICampaignNameReportRecord[])
            ?.filter(campaign => campaign[BeeswaxReportProperties.IMPRESSIONS] > 0)
            ?.map(campaign => ({
              value: campaign[BeeswaxReportProperties.CAMPAIGN_ID],
              label: campaign[BeeswaxReportProperties.CAMPAIGN_NAME],
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
        }
      })
      .addCase(getPlacementDeliveryReport.pending, pendingCaseHandler('placementDeliveryReport', []))
      .addCase(getPlacementDeliveryReport.rejected, rejectedCaseHandler('placementDeliveryReport', []))
      .addCase(getPlacementDeliveryReport.fulfilled, (state, action) => {
        if (state.placementDeliveryReportRequestId === action.meta.requestId) {
          const { report, filters } = action.payload;
          const shouldApply = !!(filters.campaigns?.length && !filters.placement?.length);

          state.placementDeliveryReportRequestId = null;
          if (shouldApply) {
            state.placementOptions = placementReportToControlOptions(filters, report);
          }
          state.placementDeliveryReport = report;
        }
      });
  },
});

export const { resetBeeswaxReportsLoadingState, setBeeswaxViewType } = reportingDashboardBeeswax.actions;

export default reportingDashboardBeeswax.reducer;
